import { Navigate, Route } from 'react-router-dom';
import {
  CatalogIndexPage,
  CatalogTable,
  CatalogTableColumnsFunc,
  CatalogTableRow,
} from '@backstage/plugin-catalog';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { apis, keycloakOIDCAuthApiRef } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import { UnifiedThemeProvider } from '@backstage/theme';
import { intiveTheme } from './themes/intive';
import {
  AlertDisplay,
  OAuthRequestDialog,
  SignInPage,
  TableProps,
  TableOptions,
} from '@backstage/core-components';
import { CatalogUnprocessedEntitiesPage } from '@backstage/plugin-catalog-unprocessed-entities';
import { AppRouter, FlatRoutes, FeatureFlagged } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { NotificationsPage } from '@backstage/plugin-notifications';
import { RbacPage } from '@backstage-community/plugin-rbac';
import {
  ProjectRegionPicker,
  ProjectIndustryPicker,
  ProjectTechnologyPicker,
  ProjectTechnologyTagPicker,
} from '@internal/plugin-pulse';
import {
  EntityKindPicker,
  useStarredEntities,
} from '@backstage/plugin-catalog-react';
import { Typography } from '@material-ui/core';
import { visuallyHidden } from '@mui/utils';
import { withStyles } from '@material-ui/core/styles';
import Star from '@material-ui/icons/Star';
import StarBorder from '@material-ui/icons/StarBorder';
import { DevToolsPage } from '@backstage/plugin-devtools';
import { devToolsPlugin } from '@backstage/plugin-devtools';
import { customDevToolsPage } from './components/devtools/CustomDevToolsPage';
import { RequirePermission } from '@backstage/plugin-permission-react';
import pulsePlugin from '@internal/plugin-pulse/alpha';
import { devToolsAdministerPermission } from '@backstage/plugin-devtools-common';
import {
  projectAccountColumn,
  projectTechnologyColumn,
  projectIndustryColumn,
  projectRegionColumn,
  technologyTagsColumn,
  technologyWebsiteColumn,
  technologyDocumentationColumn,
} from '@internal/plugin-pulse';
import { createApp } from '@backstage/frontend-defaults';
import {
  convertLegacyAppRoot,
  convertLegacyAppOptions,
  convertLegacyPlugin,
  convertLegacyPageExtension,
} from '@backstage/core-compat-api';
import buiThemerPlugin from '@backstage/plugin-mui-to-bui';
import authPlugin from '@backstage/plugin-auth';
import catalogPlugin from '@backstage/plugin-catalog/alpha';
import './styles.css';

const BlueStar = withStyles({
  root: {
    color: '#007ac9',
  },
})(Star);

const convertedDevtoolsPlugin = convertLegacyPlugin(devToolsPlugin, {
  extensions: [
    convertLegacyPageExtension(DevToolsPage, {
      name: 'index',
      path: '/devtools',
    }),
  ],
});

const customActions: TableProps<CatalogTableRow>['actions'] = [
  ({ entity }) => {
    const { isStarredEntity, toggleStarredEntity } = useStarredEntities();
    const isStarred = isStarredEntity(entity);
    const title = isStarred ? 'Remove from favorites' : 'Add to favorites';

    return {
      cellStyle: { paddingLeft: '1em' },
      icon: () => (
        <>
          <Typography style={visuallyHidden}>{title}</Typography>
          {isStarred ? <BlueStar /> : <StarBorder />}
        </>
      ),
      tooltip: title,
      onClick: () => toggleStarredEntity(entity),
    };
  },
];

const convertedOptionsModule = convertLegacyAppOptions({
  /* legacy options such as apis, icons, plugins, components, themes and featureFlags */
  apis,
  plugins: [orgPlugin],
  themes: [
    {
      id: 'intive-theme-light',
      title: 'intive theme light',
      variant: 'light',
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={intiveTheme} children={children} />
      ),
    },
  ],
  components: {
    SignInPage: props => {
      const configApi = useApi(configApiRef);
      if (configApi.getString('auth.environment') === 'local') {
        return <SignInPage {...props} auto providers={['guest']} />;
      }
      return (
        <SignInPage
          {...props}
          provider={{
            id: 'keycloak-oidc',
            title: 'Keycloak',
            message: 'Sign in using Keycloak',
            apiRef: keycloakOIDCAuthApiRef,
          }}
        />
      );
    },
  },
});

const customColumnsFunc: CatalogTableColumnsFunc = entityListContext => {
  const kind = entityListContext.filters.kind?.value;

  // Project columns
  if (kind === 'project') {
    return [
      CatalogTable.columns.createNameColumn(),
      projectAccountColumn(),
      projectTechnologyColumn(),
      projectIndustryColumn(),
      projectRegionColumn(),
    ];
  }

  // Technology columns
  if (kind === 'technology') {
    return [
      CatalogTable.columns.createNameColumn(),
      technologyTagsColumn(),
      technologyWebsiteColumn(),
      technologyDocumentationColumn(),
    ];
  }

  // Region columns
  if (kind === 'region') {
    return [
      CatalogTable.columns.createNameColumn(),
      CatalogTable.columns.createMetadataDescriptionColumn(),
    ];
  }

  // Industry columns
  if (kind === 'industry') {
    return [
      CatalogTable.columns.createNameColumn(),
      CatalogTable.columns.createMetadataDescriptionColumn(),
    ];
  }

  // Default columns for other entity types
  return CatalogTable.defaultColumnsFunc(entityListContext);
};

const tableOptions: TableOptions = {
  padding: 'default',
};

const catalogPluginOverride = catalogPlugin.withOverrides({
  extensions: [
    catalogPlugin.getExtension('page:catalog').override({
      params: {
        loader: async () => (
          <CatalogIndexPage
            initialKind="project"
            tableOptions={tableOptions}
            filters={
              <>
                <FeatureFlagged with="catalog-show-users-and-groups">
                  <EntityKindPicker
                    initialFilter="project"
                    allowedKinds={[
                      'project',
                      'technology',
                      'region',
                      'industry',
                      'group',
                      'user',
                    ]}
                  />
                </FeatureFlagged>
                <FeatureFlagged without="catalog-show-users-and-groups">
                  <EntityKindPicker
                    initialFilter="project"
                    allowedKinds={[
                      'project',
                      'technology',
                      'region',
                      'industry',
                    ]}
                  />
                </FeatureFlagged>
                <FeatureFlagged without="experimental-technology-tag-based-filtering">
                  <ProjectTechnologyPicker />
                </FeatureFlagged>
                <FeatureFlagged with="experimental-technology-tag-based-filtering">
                  <ProjectTechnologyTagPicker />
                </FeatureFlagged>
                <ProjectIndustryPicker />
                <ProjectRegionPicker />
              </>
            }
            columns={customColumnsFunc}
            actions={customActions}
          />
        ),
      },
    }),
    // Override the default about card
    // See https://backstage.io/docs/frontend-system/building-apps/migrating#im-seeing-duplicate-cards-for-entity-pages
    catalogPlugin.getExtension('entity-card:catalog/about').override({
      params: {
        loader: async () => <></>,
      },
    }),
  ],
});

const routes = (
  <FlatRoutes>
    <Route path="/" element={<Navigate to="catalog" />} />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/rbac" element={<RbacPage />} />
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/notifications" element={<NotificationsPage />} />
    <Route
      path="/catalog-unprocessed-entities"
      element={<CatalogUnprocessedEntitiesPage />}
    />
    <Route
      path="/devtools"
      element={
        <RequirePermission permission={devToolsAdministerPermission}>
          <DevToolsPage />
        </RequirePermission>
      }
    >
      {customDevToolsPage}
    </Route>
  </FlatRoutes>
);

const convertedRootFeatures = convertLegacyAppRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
  </>,
  { entityPage },
);

const app = createApp({
  features: [
    catalogPluginOverride,
    authPlugin,
    pulsePlugin,
    convertedOptionsModule,
    buiThemerPlugin,
    ...convertedRootFeatures,
    convertedDevtoolsPlugin,
  ],

  bindRoutes({ bind }) {
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
    bind(catalogPlugin.externalRoutes, {});
  },
});

export default app.createRoot();
